import React, { useState, useMemo } from "react";
import { useSkills } from "../resume/useResumeData";
import DashboardLoader from "./DashboardLoader";
import Error from "../../ui/Error";
import EditSvg from "../../ui/EditSvg";
import DeleteSvg from "../../ui/DeleteSvg";
import SkillsModal from "./SkillsModal";
import DeleteConfirmationModal from "./DeleteConfirmationModal";



const SkillsManager = () => {
    const { skills, isLoading, deleteSkill, deleteSkillsBulk, isDeleting, isDeletingBulk } = useSkills();
    const [editingItem, setEditingItem] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Delete Modal State
    const [deleteModal, setDeleteModal] = useState({
        isOpen: false,
        type: 'single', // 'single' or 'bulk'
        id: null,
        count: 0
    });

    // New States
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedIds, setSelectedIds] = useState([]);

    // Filter skills based on search
    const filteredSkills = useMemo(() => {
        if (!skills) return [];
        if (!searchQuery) return skills;
        const lowerQuery = searchQuery.toLowerCase();
        return skills.filter(
            (skill) =>
                skill.name.toLowerCase().includes(lowerQuery) ||
                skill.category.toLowerCase().includes(lowerQuery)
        );
    }, [skills, searchQuery]);

    const handleDeleteClick = (id) => {
        const skill = skills.find(s => s.id === id);
        setDeleteModal({
            isOpen: true,
            type: 'single',
            id: id,
            title: skill?.name || 'this skill'
        });
    };

    const handleBulkDeleteClick = () => {
        if (selectedIds.length === 0) return;
        setDeleteModal({
            isOpen: true,
            type: 'bulk',
            id: null,
            count: selectedIds.length,
            title: `${selectedIds.length} selected skills`
        });
    };

    const handleConfirmDelete = () => {
        if (deleteModal.type === 'single' && deleteModal.id) {
            deleteSkill(deleteModal.id, {
                onSuccess: () => setDeleteModal(prev => ({ ...prev, isOpen: false }))
            });
        } else if (deleteModal.type === 'bulk') {
            deleteSkillsBulk(selectedIds, {
                onSuccess: () => {
                    setSelectedIds([]);
                    setDeleteModal(prev => ({ ...prev, isOpen: false }));
                }
            });
        }
    };

    const handleEdit = (item) => {
        setEditingItem(item);
        setIsModalOpen(true);
    };

    const handleAddNew = () => {
        setEditingItem(null);
        setIsModalOpen(true);
    };

    // Toggle selection of a single item
    const toggleSelection = (id) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
        );
    };

    // Select/Deselect all visible items
    const toggleSelectAll = () => {
        if (selectedIds.length === filteredSkills.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(filteredSkills.map(s => s.id));
        }
    };

    if (isLoading) return <DashboardLoader />;
    if (!skills) return <Error />;

    return (
        <div>
            <h1 className="mb-6 text-2xl font-bold text-gray-800 dark:text-white">Skills</h1>

            {/* Search and Actions Bar */}
            <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-4">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Search skills..."
                            className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 pl-10 text-sm focus:border-primary focus:outline-none dark:border-gray-700 dark:bg-[#1a1f2e] dark:text-white sm:w-64"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        <svg className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </div>

                    {filteredSkills.length > 0 && (
                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                checked={selectedIds.length > 0 && selectedIds.length === filteredSkills.length}
                                onChange={toggleSelectAll}
                            />
                            <span className="text-sm text-gray-500 dark:text-gray-400">Select All</span>
                        </div>
                    )}
                </div>

                <div className="flex items-center gap-3">
                    {selectedIds.length > 0 && (
                        <button
                            onClick={handleBulkDeleteClick}
                            disabled={isDeletingBulk}
                            className="group relative flex items-center gap-2 overflow-hidden rounded-lg bg-red-500 px-4 py-2 text-sm font-medium text-white shadow-md transition-all duration-300 hover:bg-red-600 hover:shadow-lg hover:shadow-red-500/30 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            <span className="relative z-10 flex items-center gap-2">
                                <DeleteSvg className="h-4 w-4 transition-transform duration-300 group-hover:scale-110" />
                                Delete ({selectedIds.length})
                            </span>
                        </button>
                    )}
                    <button
                        onClick={handleAddNew}
                        className="group relative flex items-center gap-2 overflow-hidden rounded-lg bg-gradient-to-r from-primary to-secondary px-4 py-2 text-sm font-medium text-white shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/30 active:translate-y-0 active:scale-95"
                    >
                        <span className="relative z-10 flex items-center gap-2">
                            <svg className="h-4 w-4 transition-transform duration-300 group-hover:rotate-90" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            Add Skill
                        </span>
                        <div className="absolute inset-0 z-0 bg-gradient-to-l from-primary to-secondary opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {filteredSkills.map((item) => {
                    const isSelected = selectedIds.includes(item.id);
                    return (
                        <div
                            key={item.id}
                            onClick={() => toggleSelection(item.id)}
                            className={`
                    relative cursor-pointer transition-all duration-200
                    flex items-center justify-between rounded-xl border p-4 shadow-sm 
                    ${isSelected
                                    ? 'border-primary bg-primary/5 dark:bg-primary/10 ring-1 ring-primary'
                                    : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-[#1a1f2e]'
                                }
                    dark:text-white
                `}
                        >
                            {/* Selection Checkbox Overlay */}
                            <div className={`
                    absolute -top-2 -right-2 h-6 w-6 rounded-full border-2 flex items-center justify-center transition-colors z-20 bg-white dark:bg-[#1a1f2e]
                    ${isSelected ? 'border-primary' : 'border-gray-200 dark:border-gray-600'}
                `}>
                                {isSelected && (
                                    <div className="h-3 w-3 rounded-full bg-primary" />
                                )}
                            </div>
                            <div className="flex-1 pr-8">
                                <div className="flex items-center gap-2">
                                    <h4 className="font-bold">{item.name}</h4>
                                </div>
                                <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
                                    <div
                                        className="h-full bg-gradient-to-r from-primary to-secondary"
                                        style={{ width: `${item.percentage}%` }}
                                    />
                                </div>
                                <div className="flex items-center justify-between mt-1">
                                    <p className="text-xs capitalize text-gray-500 dark:text-gray-400">
                                        {item.category}
                                    </p>
                                    <span className="text-xs font-semibold text-primary">{item.percentage}%</span>
                                </div>
                            </div>

                            {/* Action Buttons - Stop Propagation to prevent selection toggle when clicking buttons */}
                            <div className="flex flex-col gap-2 border-l border-gray-100 pl-3 ml-2 dark:border-gray-700" onClick={(e) => e.stopPropagation()}>
                                <button
                                    onClick={() => handleEdit(item)}
                                    className="transform rounded-full p-2 text-blue-500 transition-all duration-300 hover:bg-blue-50 hover:scale-110 hover:shadow-md dark:hover:bg-blue-900/20"
                                    title="Edit Skill"
                                >
                                    <EditSvg className="h-4 w-4" />
                                </button>
                                <button
                                    onClick={() => handleDeleteClick(item.id)}
                                    disabled={isDeleting}
                                    className="transform rounded-full p-2 text-red-500 transition-all duration-300 hover:bg-red-50 hover:scale-110 hover:shadow-md dark:hover:bg-red-900/20"
                                    title="Delete Skill"
                                >
                                    <DeleteSvg className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    )
                })}
            </div>

            {filteredSkills.length === 0 && (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                    {searchQuery ? `No skills found matching "${searchQuery}"` : "No skills yet — add your first one above."}
                </div>
            )}

            {isModalOpen && (
                <SkillsModal
                    itemToEdit={editingItem}
                    onClose={() => setIsModalOpen(false)}
                />
            )}

            <DeleteConfirmationModal
                isOpen={deleteModal.isOpen}
                onClose={() => setDeleteModal(prev => ({ ...prev, isOpen: false }))}
                onConfirm={handleConfirmDelete}
                title={deleteModal.title}
                entityLabel="Skill"
            />
        </div>
    );
};

export default SkillsManager;
